import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import DemoSignUpButton from '../components/buttons/DemoSignUpButton';
import { facebookPixelEvents } from '../utils/facebookPixel';
import { PAGE_TITLES } from '../constants/pageTracking';
import { trackPageView } from '../utils/trackingUtils';

export default function OrderBusinessChallenge() {
    const [selectedOption, setSelectedOption] = useState<'student' | 'family' | null>(null);

    // Track page view with improved tracking
    useEffect(() => {
        trackPageView('ORDER', 'order');
        window.scrollTo(0, 0);
    }, []);

    // Track section views
    const trackSectionView = (sectionName: string) => {
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'section_view', {
                page_title: PAGE_TITLES.ORDER.tracking,
                section_name: sectionName
            });
        }
    };

    // Add section tracking
    useEffect(() => {
        const sections = [
            { id: 'order-header', name: 'Order Header' },
            { id: 'payment-options', name: 'Payment Options' },
            { id: 'payment-form', name: 'Payment Form' },
            { id: 'demo-section', name: 'Demo Section' }
        ];

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const sectionName = sections.find(s => s.id === entry.target.id)?.name;
                        if (sectionName) {
                            trackSectionView(sectionName);
                        }
                    }
                });
            },
            { threshold: 0.5 }
        );

        sections.forEach(section => {
            const element = document.getElementById(section.id);
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, []);

    // Update existing handlers with additional tracking
    const handleOptionSelect = async (option: 'student' | 'family') => {
        setSelectedOption(option);
        
        // Facebook Pixel checkout initiation
        facebookPixelEvents.initiateCheckout({
          content_name: `Business Challenge ${option === 'student' ? 'Individual' : 'Group'} Access`,
          content_category: 'business_challenge',
          value: option === 'student' ? 69 : 149,
          currency: 'USD'
        });

        // Add Google Analytics event
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'begin_checkout', {
                page_title: PAGE_TITLES.ORDER.tracking,
                product_variant: option,
                value: option === 'student' ? 69 : 149,
                currency: 'USD'
            });
        }
    };

    // Add back handlePaymentClick with improved tracking
    const handlePaymentClick = async (option: 'student' | 'family') => {
        // Facebook Pixel payment info tracking
        facebookPixelEvents.addPaymentInfo({
          content_name: `Business Challenge ${option === 'student' ? 'Individual' : 'Group'} Access`,
          content_category: 'business_challenge',
          value: option === 'student' ? 69 : 149,
          currency: 'USD'
        });

        // Add Google Analytics payment tracking
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'add_payment_info', {
                page_title: PAGE_TITLES.ORDER.tracking,
                product_variant: option,
                value: option === 'student' ? 69 : 149,
                currency: 'USD',
                payment_type: 'hubspot_payments'
            });
        }
    };

    return (
        <>
            <Helmet>
                <title>{PAGE_TITLES.ORDER.seo}</title>
                <link rel="canonical" href="https://homeschool.emploskills.com/order-business-challenge" />
                <meta 
                    name="description" 
                    content="Sign up for the EmploSkills Virtual Business Challenge. Choose between individual ($69) or group access ($149) for our global business competition." 
                />
                <meta 
                    name="keywords" 
                    content="business challenge registration, homeschool competition, virtual business simulation, teen entrepreneurship" 
                />
                
                {/* Open Graph */}
                <meta property="og:title" content="Join the Virtual Business Challenge | EmploSkills" />
                <meta property="og:description" content="Sign up for the EmploSkills Virtual Business Challenge. Individual or group access available." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://homeschool.emploskills.com/order-business-challenge" />
                
                {/* Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": "Virtual Business Challenge Access",
                        "description": "Access to EmploSkills Virtual Business Challenge competition",
                        "offers": [
                            {
                                "@type": "Offer",
                                "price": "69.00",
                                "priceCurrency": "USD",
                                "name": "Individual Access"
                            },
                            {
                                "@type": "Offer",
                                "price": "149.00",
                                "priceCurrency": "USD",
                                "name": "Group Access (up to 4 students)"
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="w-full">
                    {/* Header Section */}
                    <div id="order-header" className="text-center mb-12">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">
                            Join the Business Challenge
                        </h1>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Take the first step towards mastering business skills and competing 
                            in our virtual business simulation.
                        </p>
                    </div>

                    {/* Payment Options Section */}
                    <div id="payment-options" className="w-full mt-12">
                            <h3 className="text-xl font-semibold mb-4 text-center">
                                Choose Your Access
                            </h3>
                            <div className="flex flex-col sm:flex-row justify-center gap-4">
                                <a
                                    href="https://app-eu1.hubspot.com/payments/HQ7mNMff?referrer=PAYMENT_LINK"
                                    onClick={() => {
                                        handleOptionSelect('student');
                                        handlePaymentClick('student');
                                    }}
                                    className={`px-6 py-3 rounded-md border transition hover:bg-primary/90 ${
                                        selectedOption === 'student' ? 'bg-primary text-white' : 'bg-white text-primary border-primary'
                                    }`}
                                >
                                    Individual Access
                                </a>
                                <a
                                    href="https://app-eu1.hubspot.com/payments/bdtxSwsWMP?referrer=PAYMENT_LINK"
                                    onClick={() => {
                                        handleOptionSelect('family');
                                        handlePaymentClick('family');
                                    }}
                                    className={`px-6 py-3 rounded-md border transition hover:bg-primary/90 ${
                                        selectedOption === 'family' ? 'bg-primary text-white' : 'bg-white text-primary border-primary'
                                    }`}
                                >
                                    Group Access
                                </a>
                            </div>
                            {selectedOption && (

                                    <iframe
                                        src={selectedOption === 'student' ? 'https://app-eu1.hubspot.com/payments/HQ7mNMff?referrer=PAYMENT_LINK' : 'https://app-eu1.hubspot.com/payments/bdtxSwsWMP?referrer=PAYMENT_LINK'}
                                        className="w-full border-0"
                                        style={{ height: '100vh', overflow: 'hidden' }}
                                        title="Payment Form"
                                    ></iframe>

                            )}
                    </div>

                    {/* Additional Information */}
                    <div className="mt-12 text-center text-gray-600">
                        <p className="mb-4">
                            Need help? Contact us at{' '}
                            <a href="mailto:contact@emploskills.com" className="text-primary hover:underline">
                                contact@emploskills.com
                            </a>
                        </p>
                    </div>
                </div>
            </div>

            {/* Add a section for the training simulation option */}
            <div id="demo-section" className="w-full mt-12">
                <div className="bg-gray-100 p-8 rounded-lg text-center">
                    <h3 className="text-xl font-semibold mb-4">
                        Want to Test First?
                    </h3>
                    <p className="mb-6">
                        Try our demo simulation to get familiar with the platform before joining the competition.
                    </p>
                    <DemoSignUpButton />
                </div>
            </div>
        </>
    );
}